import React, { useState, useEffect } from 'react';
import {
  X,
  Search,
  Briefcase,
  MapPin,
  Calendar,
  Check,
  Eye,
  User,
  Mail,
  Phone,
  Plus,
  Brain
} from 'lucide-react';
import { useUser } from '../contexts/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const JobDetailsSubmitModal = ({ isOpen, onClose, job, onSubmitSuccess }) => {
  const { currentUser } = useUser(); // Get current user from context
  const [candidates, setCandidates] = useState([]);
  const [existingSubmissions, setExistingSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [matchingMode, setMatchingMode] = useState('semantic');
  const [semanticMatchesAvailable, setSemanticMatchesAvailable] = useState(true);
  const [displayLimit, setDisplayLimit] = useState(10);

  const checkJobDataQuality = () => {
    // Defensive: Check if job exists first
    if (!job) {
      return {
        hasSkills: false,
        hasDescription: false,
        isGoodQuality: false
      };
    }
    
    const hasRequiredSkills = job.required_skills && job.required_skills.length > 0;
    const hasPreferredSkills = job.preferred_skills && job.preferred_skills.length > 0;
    const hasDescription = job.description && job.description.length > 100;
    
    return {
      hasSkills: hasRequiredSkills || hasPreferredSkills,
      hasDescription,
      isGoodQuality: (hasRequiredSkills || hasPreferredSkills) && hasDescription
    };
  };

  const dataQuality = checkJobDataQuality();

  useEffect(() => {
    if (isOpen && job) {
      fetchCandidatesAndSubmissions();
    }
  }, [isOpen, job, matchingMode]);

  const fetchCandidatesAndSubmissions = async () => {
    if (!job?.id) return;
    
    setLoading(true);
    try {
      const submissionsResponse = await fetch(`${BACKEND_URL}/api/submissions?job_id=${job.id}`);
      if (submissionsResponse.ok) {
        const submissionsData = await submissionsResponse.json();
        const submissions = submissionsData.submissions || submissionsData;
        setExistingSubmissions(submissions);
      }

      let candidatesData = [];
      
      if (matchingMode === 'semantic' && dataQuality.isGoodQuality) {
        try {
          // Use correct semantic matching endpoint
          const matchResponse = await fetch(`${BACKEND_URL}/api/jobs/${job.id}/semantic-matches?top_k=50`);
          
          if (matchResponse.ok) {
            const matchData = await matchResponse.json();
            // Extract matches array and map to correct format
            candidatesData = (matchData.matches || []).map(match => ({
              ...match,
              match_score: match.match_details?.hybrid_score || 0
            }));
            setSemanticMatchesAvailable(true);
          } else {
            console.warn('Semantic matching not available, falling back to quick match');
            setSemanticMatchesAvailable(false);
            setMatchingMode('quick');
          }
        } catch (error) {
          console.error('Semantic matching failed:', error);
          setSemanticMatchesAvailable(false);
          setMatchingMode('quick');
        }
      }
      
      if (matchingMode === 'quick' || !dataQuality.isGoodQuality) {
        const candidatesResponse = await fetch(`${BACKEND_URL}/api/candidates?limit=5000`);
        if (candidatesResponse.ok) {
          candidatesData = await candidatesResponse.json();
        }
      }

      setCandidates(candidatesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractSkillsFromJobText = (job) => {
    const text = `${job.title || ''} ${job.description || ''}`.toLowerCase();
    const commonSkills = [
      'java', 'python', 'javascript', 'react', 'angular', 'vue', 'node.js', 'sql',
      'aws', 'azure', 'docker', 'kubernetes', 'agile', 'scrum', 'api', 'rest'
    ];
    
    const foundSkills = [];
    commonSkills.forEach(keyword => {
      if (text.includes(keyword)) {
        foundSkills.push(keyword);
      }
    });
    
    return foundSkills;
  };

  const calculateSkillMatch = (candidate) => {
    // Guard against null job
    if (!job) {
      return {
        percentage: 0,
        matchedSkills: [],
        requiredMatches: 0,
        preferredMatches: 0
      };
    }
    
    const candidateSkills = candidate.skills || candidate.technical_skills || [];
    let jobRequiredSkills = job.required_skills || [];
    let jobPreferredSkills = job.preferred_skills || [];
    
    if (jobRequiredSkills.length === 0 && jobPreferredSkills.length === 0) {
      const extractedSkills = extractSkillsFromJobText(job);
      jobPreferredSkills = extractedSkills;
    }
    
    const normalizedCandidateSkills = candidateSkills.map(s => s.toLowerCase().trim());
    const normalizedRequired = jobRequiredSkills.map(s => s.toLowerCase().trim());
    const normalizedPreferred = jobPreferredSkills.map(s => s.toLowerCase().trim());
    
    let matchCount = 0;
    
    const requiredMatches = normalizedRequired.filter(skill => 
      normalizedCandidateSkills.includes(skill)
    ).length;
    matchCount += requiredMatches * 2;
    
    const preferredMatches = normalizedPreferred.filter(skill => 
      normalizedCandidateSkills.includes(skill)
    ).length;
    matchCount += preferredMatches;
    
    const weightedTotal = (normalizedRequired.length * 2) + normalizedPreferred.length;
    const matchPercentage = weightedTotal > 0 ? Math.round((matchCount / weightedTotal) * 100) : 0;
    
    return {
      percentage: matchPercentage,
      requiredMatches,
      requiredTotal: normalizedRequired.length,
      preferredMatches,
      preferredTotal: normalizedPreferred.length,
      totalMatches: requiredMatches + preferredMatches
    };
  };

  const isCandidateAlreadySubmitted = (candidateId) => {
    return existingSubmissions.some(sub => sub.candidate_id === candidateId);
  };

  const filteredCandidates = candidates.filter(candidate => {
    if (isCandidateAlreadySubmitted(candidate.id)) return false;
    
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    const name = (candidate.name || candidate.full_name || '').toLowerCase();
    const email = (candidate.email || '').toLowerCase();
    const skills = (candidate.skills || candidate.technical_skills || []).join(' ').toLowerCase();
    
    return name.includes(search) || email.includes(search) || skills.includes(search);
  });

  // Always calculate match scores for display purposes
  const sortedCandidates = filteredCandidates
    .map(candidate => {
      // If semantic mode and score exists, use it; otherwise calculate skill match
      const matchScore = (matchingMode === 'semantic' && candidate.match_score)
        ? candidate.match_score
        : calculateSkillMatch(candidate);
      
      return {
        ...candidate,
        matchScore: typeof matchScore === 'number' ? { percentage: matchScore } : matchScore,
        displayScore: typeof matchScore === 'number' ? matchScore : matchScore.percentage
      };
    })
    .sort((a, b) => (b.displayScore || 0) - (a.displayScore || 0))
    .slice(0, displayLimit);

  const toggleCandidateSelection = (candidateId) => {
    setSelectedCandidates(prev =>
      prev.includes(candidateId)
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleSubmit = async () => {
    if (selectedCandidates.length === 0) {
      alert('Please select at least one candidate');
      return;
    }

    // CRITICAL: Validate that we have a current user
    if (!currentUser || !currentUser.id) {
      alert('Error: No user context available. Please refresh and select a persona.');
      console.error('No currentUser in context:', currentUser);
      return;
    }

    setSubmitting(true);
    try {
      const submissionPromises = selectedCandidates.map(async (candidateId) => {
        const candidate = candidates.find(c => c.id === candidateId);
        if (!candidate) return false;

        const matchScore = matchingMode === 'semantic'
          ? (candidate.match_score || 0)
          : calculateSkillMatch(candidate).percentage;

        const submissionData = {
          candidate_id: candidateId,
          candidate_name: candidate.name || candidate.full_name || 'Unknown Candidate',
          job_id: job.id,
          job_title: job.title || 'Unknown Job',
          recruiter_id: currentUser.id, // ✅ Use actual user ID from context
          recruiter_name: currentUser.full_name || currentUser.name, // ✅ Use actual user name
          submission_notes: `Submitted to ${job.title || 'job'}`,
          priority_level: 'normal',
          match_score: matchScore
        };

        const response = await fetch(`${BACKEND_URL}/api/submissions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(submissionData)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Submission failed:', errorData);
          return false;
        }

        return true;
      });

      const results = await Promise.all(submissionPromises);
      const successCount = results.filter(r => r).length;

      if (successCount > 0) {
        alert(`Successfully submitted ${successCount} candidate(s) to ${job.title}`);
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
        setSelectedCandidates([]);
        setSearchTerm('');
        onClose();
      } else {
        alert('Failed to submit candidates. Please check the console for details.');
      }
    } catch (error) {
      console.error('Error submitting candidates:', error);
      alert('Error submitting candidates: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !job) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Briefcase className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">{job.title || 'Job Details'}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'details'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Job Details
          </button>
          <button
            onClick={() => setActiveTab('submit')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'submit'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Submit Candidates ({selectedCandidates.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Company</p>
                  <p className="font-medium text-gray-900">{job.client_company || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <div className="flex items-center space-x-1">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <p className="font-medium text-gray-900">{job.location || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">Description</p>
                <p className="text-gray-700 whitespace-pre-wrap">{job.description || 'No description available'}</p>
              </div>

              {job.required_skills && job.required_skills.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Required Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {job.required_skills.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {job.preferred_skills && job.preferred_skills.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Preferred Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {job.preferred_skills.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {!dataQuality.isGoodQuality && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <p className="text-sm text-yellow-700">
                    ⚠️ This job has limited skill data. Using quick skill-based matching instead of AI semantic matching.
                  </p>
                </div>
              )}

              {dataQuality.isGoodQuality && (
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      checked={matchingMode === 'semantic'}
                      onChange={() => setMatchingMode('semantic')}
                      className="form-radio"
                    />
                    <span>AI Semantic Match</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      checked={matchingMode === 'quick'}
                      onChange={() => setMatchingMode('quick')}
                      className="form-radio"
                    />
                    <span>Quick Skill Match</span>
                  </label>
                </div>
              )}

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search candidates by name, email, or skills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Loading candidates...</p>
                </div>
              ) : sortedCandidates.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No candidates found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedCandidates.map(candidate => {
                    const isSelected = selectedCandidates.includes(candidate.id);
                    const matchScore = candidate.displayScore || candidate.matchScore?.percentage || 0;

                    return (
                      <div
                        key={candidate.id}
                        onClick={() => toggleCandidateSelection(candidate.id)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              {isSelected && <Check className="h-5 w-5 text-blue-600" />}
                              <h4 className="font-medium text-gray-900">
                                {candidate.name || candidate.full_name}
                              </h4>
                              <span className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                                matchScore >= 70 ? 'bg-green-100 text-green-800' :
                                matchScore >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                matchScore > 0 ? 'bg-gray-100 text-gray-800' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                <Brain className="h-3 w-3" />
                                <span>{matchScore > 0 ? `${matchScore}%` : 'No'} Match</span>
                              </span>
                            </div>
                            <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                              {candidate.email && (
                                <div className="flex items-center space-x-1">
                                  <Mail className="h-4 w-4" />
                                  <span>{candidate.email}</span>
                                </div>
                              )}
                              {candidate.phone && (
                                <div className="flex items-center space-x-1">
                                  <Phone className="h-4 w-4" />
                                  <span>{candidate.phone}</span>
                                </div>
                              )}
                            </div>
                            {(candidate.skills || candidate.technical_skills) && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {(candidate.skills || candidate.technical_skills).slice(0, 5).map((skill, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {filteredCandidates.length > displayLimit && (
                    <button
                      onClick={() => setDisplayLimit(prev => prev + 10)}
                      className="w-full py-2 text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Show More
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          {activeTab === 'submit' && (
            <button
              onClick={handleSubmit}
              disabled={submitting || selectedCandidates.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {submitting ? (
                <span>Submitting...</span>
              ) : (
                <>
                  <Check className="h-5 w-5" />
                  <span>Submit {selectedCandidates.length} Candidate(s)</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetailsSubmitModal;
